import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import AccessMethodsBadge from "./AccessMethodsBadge";
import DamageCombinationsBadge from "./DamageCombinationsBadge";

/**
 * Generic Structure Node Component
 * Renders any level of bridge structure hierarchy
 *
 * @param {Object} element - Structure element from bridge_structure
 * @param {Object} bridge - Bridge object with levelLabels configuration
 * @param {Set} expandedNodes - Set of expanded node IDs
 * @param {Function} toggleNode - Function to toggle node expansion
 * @param {Function} onAdd - Function to handle adding child element
 * @param {Function} onEdit - Function to handle editing element
 * @param {Function} onDelete - Function to handle deleting element
 * @param {Function} getLevelLabel - Function to get level label
 * @param {Function} isLevelEnabled - Function to check if level is enabled
 */
const StructureNode = React.memo(
  ({
    element,
    bridge,
    expandedNodes,
    toggleNode,
    onAdd,
    onEdit,
    onDelete,
    onEditAccessMethods,
    onEditDamageCombinations,
    getLevelLabel,
    isLevelEnabled,
    loadingChildren = new Set(),
    loadedChildren = new Set(),
  }) => {
    const nodeId = `structure-${element.id}`;
    const isExpanded = expandedNodes.has(nodeId);
    const levelNumber = element.levelNumber || element.level_number;
    const levelLabel = getLevelLabel(levelNumber);
    const nextLevelNumber = levelNumber + 1;
    const hasChildren = element.children && element.children.length > 0;
    const hasLoadedChildren = loadedChildren.has(element.id);
    const isLoadingChildren = loadingChildren.has(element.id);
    const canHaveChildren =
      element.hasChildren !== false &&
      (hasChildren || isLevelEnabled(nextLevelNumber));
    const nextLevelEnabled = isLevelEnabled(nextLevelNumber);
    const levelLabelsConfig = bridge?.levelLabels || {};
    const nextLevelHoldsAreaData =
      nextLevelEnabled &&
      Boolean(levelLabelsConfig[`level${nextLevelNumber}`]?.holdsAreaData);

    const getNodeAreaContribution = (node) => {
      if (!node) return 0;

      const ownArea = parseFloat(node.totalArea ?? node.total_area ?? 0);
      if (Number.isFinite(ownArea) && ownArea > 0) {
        return ownArea;
      }

      const persistedDesc = parseFloat(
        node.descendantTotalArea ?? node.descendant_total_area ?? 0,
      );
      if (Number.isFinite(persistedDesc) && persistedDesc > 0) {
        return persistedDesc;
      }

      const children = Array.isArray(node.children) ? node.children : [];
      if (!children.length) return 0;

      return children.reduce(
        (sum, child) => sum + getNodeAreaContribution(child),
        0,
      );
    };

    const parseArea = (value) => {
      const parsed = parseFloat(value ?? 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    };

    // Get icon based on level
    const getIcon = () => {
      switch (levelNumber) {
        case 1:
          return "🏗️"; // Frame
        case 2:
          return "🌉"; // Span/Pillar
        case 3:
          return "🧱"; // Panel Bay/Slab
        case 4:
          return "🔧"; // Component
        default:
          return "⚙️"; // Other levels
      }
    };

    // Get condition rating for exposure class level elements
    const getConditionRating = () => {
      if (!bridge || !element) return null;

      // Check if this element is at exposure class level
      const levelConfig = bridge.levelLabels || {};
      const levelKey = `level${levelNumber}`;
      const config = levelConfig[levelKey];

      if (!config || !config.holdsExposureClassData) {
        return null;
      }

      // Get degradation percentage
      const degradationPercent =
        element.currentDegradationPercent ||
        element.current_degradation_percent;
      if (degradationPercent === null || degradationPercent === undefined) {
        return null;
      }

      // Get scale configuration from bridge
      let scaleConfig =
        bridge.conditionRatingScale || bridge.condition_rating_scale;
      if (typeof scaleConfig === "string") {
        try {
          scaleConfig = JSON.parse(scaleConfig);
        } catch (e) {
          return null;
        }
      }

      if (!scaleConfig || !scaleConfig.levels) {
        return null;
      }

      // Map degradation to rating
      const degradation = Math.max(
        0,
        Math.min(100, parseFloat(degradationPercent)),
      );
      for (const level of scaleConfig.levels) {
        if (degradation >= level.min && degradation < level.max) {
          return {
            label: level.label,
            name: level.name,
            color: level.color,
            degradationPercent: degradation,
          };
        }
      }

      // Handle edge case: exactly 100%
      const lastLevel = scaleConfig.levels[scaleConfig.levels.length - 1];
      if (degradation === 100 && degradation >= lastLevel.min) {
        return {
          label: lastLevel.label,
          name: lastLevel.name,
          color: lastLevel.color,
          degradationPercent: degradation,
        };
      }

      return null;
    };

    // Get badges to display
    const getBadges = () => {
      const badges = [];
      const levelConfig = bridge?.levelLabels || {};
      const currentLevelConfig = levelConfig[`level${levelNumber}`] || {};
      const isAreaDataLevel = Boolean(currentLevelConfig.holdsAreaData);

      // Condition rating badge (for exposure class level elements)
      const conditionRating = getConditionRating();
      if (conditionRating) {
        badges.push(
          <span
            key="condition"
            className="badge"
            style={{
              backgroundColor: conditionRating.color + "20",
              color: conditionRating.color,
              borderColor: conditionRating.color,
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            title={`${conditionRating.name} - ${conditionRating.degradationPercent.toFixed(2)}% degradation`}
          >
            {conditionRating.label} (
            {conditionRating.degradationPercent.toFixed(2)}%)
          </span>,
        );
      }

      // Area badge: always prefer explicit area, then persisted descendant area,
      // then loaded-children fallback so users see a consistent badge on initial load.
      const ownArea = parseArea(element.totalArea ?? element.total_area);
      const persistedDescendantArea = parseArea(
        element.descendantTotalArea ?? element.descendant_total_area,
      );
      const calculatedDescendantArea =
        Array.isArray(element.children) && element.children.length > 0
          ? element.children.reduce(
              (sum, child) => sum + getNodeAreaContribution(child),
              0,
            )
          : 0;
      const resolvedArea =
        ownArea || persistedDescendantArea || calculatedDescendantArea;

      if (resolvedArea > 0) {
        badges.push(
          <span key="area" className="badge badge-area" title="Total area">
            {resolvedArea.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            m²
          </span>,
        );
      }

      if (isAreaDataLevel) {
        const applicationType = String(
          element.applicationType ?? element.application_type ?? "",
        ).trim();
        const steelWeight = String(
          element.steelWeight ??
            element.steel_weight ??
            element.steelweight ??
            "",
        ).trim();

        badges.push(
          <span
            key="application_type"
            className="badge badge-info"
            title="Application type"
          >
            {applicationType || "APP: N/A"}
          </span>,
        );

        badges.push(
          <span
            key="steel_weight"
            className="badge badge-info"
            title="Steel weight"
          >
            {steelWeight ? `Steel: ${steelWeight}` : "Steel: N/A"}
          </span>,
        );

        // Methods badge: hover popover shows this element's method areas in m²;
        // clicking it opens the editor.
        badges.push(
          <AccessMethodsBadge
            key="access_methods"
            element={element}
            mode="area"
            onEdit={onEditAccessMethods}
          />,
        );

        // Damage badge: same interaction, separate icon and editor.
        badges.push(
          <DamageCombinationsBadge
            key="damage_combinations"
            element={element}
            mode="area"
            onEdit={onEditDamageCombinations}
          />,
        );
      }

      // Parent of the area-data level: hover popover aggregates the
      // per-value areas across its children; clicking opens the bulk editor.
      if (nextLevelHoldsAreaData) {
        badges.push(
          <AccessMethodsBadge
            key="access_methods_summary"
            element={element}
            mode="parent"
            onEdit={onEditAccessMethods}
          />,
        );
        badges.push(
          <DamageCombinationsBadge
            key="damage_combinations_summary"
            element={element}
            mode="parent"
            onEdit={onEditDamageCombinations}
          />,
        );
      }

      // Exposure class badge for levels that carry a combination but no area
      // data. On area-data rows the damage badge above already shows it (and
      // shows a mix, which this cannot), so showing both would duplicate.
      if (!isAreaDataLevel && element.corrosionDamageCombination) {
        badges.push(
          <span key="exposure" className="badge badge-info">
            {element.corrosionDamageCombination.combinationCode ||
              element.corrosionDamageCombination?.combination_code}
          </span>,
        );
      }

      // PID badge (for panel bays)
      if (element.pid) {
        badges.push(
          <span key="pid" className="badge badge-info">
            PID: {element.pid}
          </span>,
        );
      }

      // Component Group ID badge
      if (element.componentGroupId || element.component_group_id) {
        badges.push(
          <span key="group" className="badge badge-info">
            Group: {element.componentGroupId || element.component_group_id}
          </span>,
        );
      }

      // Direction/Position badges (for Level 1)
      if (levelNumber === 1) {
        const labelText = (element.elementName || element.element_name || "")
          .toString()
          .trim()
          .toUpperCase();
        const directionText = (element.direction || "")
          .toString()
          .trim()
          .toUpperCase();

        if (
          element.direction &&
          !(
            element.isMergedDirectionRoot &&
            directionText &&
            directionText === labelText
          )
        ) {
          badges.push(
            <span key="direction" className="badge badge-info">
              {element.direction}
            </span>,
          );
        }
        if (element.position) {
          badges.push(
            <span key="position" className="badge badge-info">
              {element.position}
            </span>,
          );
        }
      }

      // Children count badge
      if (hasChildren) {
        badges.push(
          <span key="count" className="node-count">
            {element.children.length}{" "}
            {getLevelLabel(nextLevelNumber).toLowerCase()}(s)
          </span>,
        );
      } else if (canHaveChildren && !hasLoadedChildren) {
        // Show indicator that children can be loaded
        badges.push(
          <span key="loadable" className="node-count" style={{ opacity: 0.6 }}>
            {getLevelLabel(nextLevelNumber).toLowerCase()}(s) available
          </span>,
        );
      }

      return badges;
    };

    return (
      <div className={`tree-node structure-node level-${levelNumber}`}>
        <div className="node-content">
          <button
            type="button"
            className="expand-btn"
            onClick={() => toggleNode(nodeId)}
            disabled={!canHaveChildren}
          >
            {isLoadingChildren
              ? "⟳"
              : canHaveChildren
                ? isExpanded
                  ? "▼"
                  : "▶"
                : "○"}
          </button>

          <span className="node-icon">{getIcon()}</span>

          <span className="node-label">
            {element.elementName || element.element_name}
          </span>

          {getBadges()}

          <div className="node-actions">
            {nextLevelEnabled && onAdd && (
              <button
                type="button"
                onClick={() => onAdd(nextLevelNumber, element)}
                className="btn-icon btn-add"
                title={`Add ${getLevelLabel(nextLevelNumber)}`}
              >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(element)}
                className="btn-icon btn-edit"
                title={`Edit ${levelLabel}`}
              >
                <FontAwesomeIcon icon={faPencil} className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(element)}
                className="btn-icon btn-delete"
                title={`Delete ${levelLabel}`}
              >
                <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="node-children">
            {isLoadingChildren ? (
              <div
                className="loading-children"
                style={{ padding: "10px", color: "#666", fontStyle: "italic" }}
              >
                Loading children...
              </div>
            ) : hasChildren ? (
              element.children.map((child) => (
                <StructureNode
                  key={child.id}
                  element={child}
                  bridge={bridge}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  onAdd={onAdd}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onEditAccessMethods={onEditAccessMethods}
                  onEditDamageCombinations={onEditDamageCombinations}
                  getLevelLabel={getLevelLabel}
                  isLevelEnabled={isLevelEnabled}
                  loadingChildren={loadingChildren}
                  loadedChildren={loadedChildren}
                />
              ))
            ) : canHaveChildren && !hasLoadedChildren ? (
              <div
                className="loading-children"
                style={{ padding: "10px", color: "#666", fontStyle: "italic" }}
              >
                Click to load children...
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  },
);

StructureNode.displayName = "StructureNode";

export default StructureNode;
